// Quote reports (round 43, release 4). Admin only.
//
// One date range, one request, five answers: how many went out and how many
// were won, month by month, by quote manager, the open pipeline, and by
// client. The rules behind each figure live on the server
// (routes/quoteReports.ts); this page only lays them out.
//
// The range defaults to the Australian financial year, because "how did we go
// this year?" in an Australian business means since 1 July.
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ApiError } from '../api';
import { Banner, Field } from '../ui';
import { PageHeader } from '../components/ui/Page';
import { formatCents, quotesApi, type QuoteReport } from '../quotesApi';
import { todayISO } from '../lib/dates';
import { SortHeader } from '../components/ui/SortHeader';
import { sortRows, useSort, type SortValue } from '../lib/sortRows';

function financialYear(today: string): { from: string; to: string } {
  const year = Number(today.slice(0, 4));
  const start = Number(today.slice(5, 7)) >= 7 ? year : year - 1;
  return { from: `${start}-07-01`, to: `${start + 1}-06-30` };
}

function monthName(month: string): string {
  const date = new Date(`${month}-01T00:00:00Z`);
  return date.toLocaleDateString('en-AU', { month: 'short', year: 'numeric', timeZone: 'UTC' });
}

const percent = (rate: number | null) => (rate === null ? 'Nothing decided' : `${Math.round(rate * 100)}%`);

export function QuoteReportsPage() {
  const initial = financialYear(todayISO());
  const [from, setFrom] = useState(initial.from);
  const [to, setTo] = useState(initial.to);
  const [report, setReport] = useState<QuoteReport | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setReport(await quotesApi.reports(from, to));
      setError(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load the reports.');
    }
  }, [from, to]);

  useEffect(() => {
    if (from && to) void load();
  }, [load, from, to]);

  const s = report?.summary;
  const cards = s
    ? [
        { label: 'Quoted', value: String(s.quoted), sub: 'first sent in the range' },
        { label: 'Won', value: String(s.won), sub: formatCents(s.wonCents) },
        { label: 'Lost', value: String(s.lost), sub: `${s.expired} expired` },
        { label: 'Win rate', value: percent(s.winRate), sub: 'won out of won and lost' },
        { label: 'Became jobs', value: String(s.converted), sub: 'of the quotes won' },
      ]
    : [];

  return (
    <>
      <PageHeader
        title="Quote reports"
        subtitle="Totals include GST where it applies. Archived quotes are left out."
        actions={
          <Link to="/app/quotes" className="btn-secondary">
            Back to quotes
          </Link>
        }
      />
      {error && <Banner kind="error">{error}</Banner>}

      <section className="panel">
        <div className="row pair max-w-md">
          <Field label="From" name="from" type="date" value={from} onChange={setFrom} />
          <Field label="To" name="to" type="date" value={to} onChange={setTo} />
        </div>
        <p className="hint">
          A quote counts as won, lost or expired in the month it was decided, and as quoted in the
          month it was first sent, so past months never change when a quote is decided later.
        </p>
      </section>

      {report && (
        <>
          <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-5">
            {cards.map((card) => (
              <div key={card.label} className="rounded-lg border border-gray-200 bg-white p-4">
                <div className="text-sm text-gray-600">{card.label}</div>
                <div className="text-2xl font-semibold tabular-nums text-gray-900">{card.value}</div>
                <div className="text-sm tabular-nums text-gray-600">{card.sub}</div>
              </div>
            ))}
          </div>

          <ReportTable
            title="Month by month"
            empty="Nothing quoted or won in this range."
            headers={['Month', 'Quoted', 'Won', 'Won value']}
            rows={report.byMonth.map((m) => ({
              cells: [monthName(m.month), String(m.quoted), String(m.won), formatCents(m.wonCents)],
              sort: [m.month, m.quoted, m.won, m.wonCents],
            }))}
          />
          <ReportTable
            title="Won by quote manager"
            empty="No quotes decided in this range."
            headers={['Quote manager', 'Won', 'Lost', 'Won value']}
            rows={report.byManager.map((m) => ({
              cells: [m.managerName ?? 'Nobody named', String(m.won), String(m.lost), formatCents(m.wonCents)],
              sort: [m.managerName, m.won, m.lost, m.wonCents],
            }))}
          />
          <ReportTable
            title="Open pipeline by quote manager"
            note="As it stands today, whatever the range above."
            empty="No open quotes."
            headers={['Quote manager', 'Draft', 'Sent', 'On hold', 'Value']}
            rows={report.pipeline.map((m) => ({
              cells: [m.managerName ?? 'Nobody named', String(m.draft), String(m.sent), String(m.onHold), formatCents(m.cents)],
              sort: [m.managerName, m.draft, m.sent, m.onHold, m.cents],
            }))}
          />
          <ReportTable
            title="By client"
            note="Quotes dated in the range, and how much of that was won."
            empty="No quotes dated in this range."
            headers={['Client', 'Quotes', 'Quoted value', 'Won', 'Won value']}
            rows={report.byClient.map((c) => ({
              cells: [c.clientName, String(c.count), formatCents(c.cents), String(c.won), formatCents(c.wonCents)],
              sort: [c.clientName, c.count, c.cents, c.won, c.wonCents],
            }))}
          />
        </>
      )}
    </>
  );
}

/**
 * A small report table that sorts like every other table in the app (round
 * 34's rule, through lib/sortRows.ts; found unsorted in review, 2026-10-04).
 * Each row carries its display cells and the raw values they sort by, so a
 * money column sorts as a number and not as "$9" after "$10". The first
 * column is a name; the rest are numbers, centred under their headings
 * (Nathan, 2026-10-14).
 */
function ReportTable({
  title,
  note,
  empty,
  headers,
  rows,
}: {
  title: string;
  note?: string;
  empty: string;
  headers: string[];
  rows: { cells: string[]; sort: SortValue[] }[];
}) {
  const { sortKey, sortDir, onSort } = useSort<string>('0', 'asc');
  const sorted = useMemo(
    () => sortRows(rows, (row) => row.sort[Number(sortKey)], sortDir),
    [rows, sortKey, sortDir]
  );
  return (
    <section className="panel">
      <h2>{title}</h2>
      {note && <p className="hint mb-3">{note}</p>}
      {rows.length === 0 ? (
        <p className="muted">{empty}</p>
      ) : (
        <div className="-mx-6 overflow-x-auto border-y border-gray-200">
          <table className="data-table">
            <thead>
              <tr>
                {headers.map((header, index) => (
                  <SortHeader
                    key={header}
                    label={header}
                    sortKey={String(index)}
                    current={sortKey}
                    dir={sortDir}
                    onSort={onSort}
                    className={index === 0 ? '' : 'text-center'}
                  />
                ))}
              </tr>
            </thead>
            <tbody>
              {sorted.map((row, rowIndex) => (
                <tr key={rowIndex}>
                  {row.cells.map((value, index) => (
                    <td
                      key={index}
                      className={index === 0 ? 'font-medium' : 'whitespace-nowrap text-center tabular-nums'}
                    >
                      {value}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
